import { LabeledText } from "@/components/ui/LabeledText";
import { Loader } from "@/components/ui/loaders";
import { TextBox } from "@/components/ui/TextBox";
import { useGenerationStatus } from "@/queries/images/use-generation-status";
import { InfoOutlined } from "@mui/icons-material";
import { Stack, Tooltip, Typography } from "@mui/material";

type Props = {
  generationError: string | null;
  generationId: string | null;
  generationTime: number | null;
  modelName: string | null;
};

// both Image and Mask have the same generation metadata fields, so we can use the same component for both
export function AssetGenerationMetadata({
  generationError,
  generationId,
  generationTime,
  modelName,
}: Props) {
  const { data, isLoading, isError } = useGenerationStatus(generationId);
  return (
    <TextBox sx={{ width: "600px" }}>
      <Stack gap={1}>
        <Typography color="textSecondary" variant="overline">
          Generation metadata
        </Typography>
        <LabeledText
          variant="body2"
          label="Generation ID"
          text={generationId}
          copyable={true}
        />
        {modelName !== null && (
          <LabeledText
            variant="body2"
            label="Model name"
            text={modelName}
            copyable={true}
          />
        )}
        {generationTime !== null && (
          <LabeledText
            variant="body2"
            label="Generation time"
            text={`${generationTime}`}
          />
        )}
        {generationError !== null && (
          <LabeledText
            variant="body2"
            label="Generation error"
            text={generationError}
          />
        )}
        {generationId && (
          <>
            <Typography color="textSecondary" variant="overline">
              Generation status
            </Typography>
            {data && (
              <>
                <LabeledText
                  variant="body2"
                  label="Overall status"
                  text={data.overall_status}
                />
                {data.error_message && (
                  <LabeledText
                    variant="body2"
                    label="Error message"
                    text={data.error_message}
                  />
                )}
              </>
            )}
            {isLoading && <Loader size={12} />}
            {isError && (
              <Stack direction={"row"} gap={0.5} alignItems={"center"}>
                <Typography color="warning" variant="body2">
                  Couldn't load generation status.
                </Typography>
                <Tooltip title="Probably nothing to worry about. The generation job might be outdated and deleted from the system.">
                  <InfoOutlined fontSize="small" color="warning" />
                </Tooltip>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </TextBox>
  );
}
